import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { LayoutGrid, List, Map, SlidersHorizontal, SearchX, RotateCcw } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Filters from "@/components/property/Filters";
import PropertyCard from "@/components/PropertyCard";
import MapView from "@/components/property/MapView";
import { api } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

const DEFAULT_FILTERS = {
  transazione: "tutti", comune: "", zona: "", tipologia: "",
  prezzoMin: "", prezzoMax: "", superficieMin: "",
  locali: 0, camere: 0, bagni: 0,
  box: false, ascensore: false, esterno: false, nuovaCostruzione: false, daRistrutturare: false,
};

const SORTS = [
  { key: "recenti", label: "Più recenti" },
  { key: "prezzo-asc", label: "Prezzo crescente" },
  { key: "prezzo-desc", label: "Prezzo decrescente" },
  { key: "superficie", label: "Maggiore superficie" },
  { key: "compatibilita", label: "Migliore compatibilità" },
];

export default function Immobili() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState("recenti");
  const [properties, setProperties] = useState([]);

  useSEO({ title: "Immobili in Vendita e Affitto a Corato | Casamore Group", description: "Scopri gli immobili in vendita e in affitto a Corato e dintorni con Casamore Group. Filtra per zona, prezzo, tipologia e caratteristiche e trova la casa giusta per te.", canonical: window.location.origin + "/immobili" });

  useEffect(() => {
    api.get("/public/properties").then((r) => setProperties(r.data)).catch(() => setProperties([]));
  }, []);

  const results = useMemo(() => {
    let list = properties.filter((p) => {
      if (filters.transazione !== "tutti" && p.transazione !== filters.transazione) return false;
      if (filters.comune && p.comune !== filters.comune) return false;
      if (filters.zona && p.zona !== filters.zona) return false;
      if (filters.tipologia && p.tipo !== filters.tipologia) return false;
      if (filters.prezzoMin && p.prezzo < Number(filters.prezzoMin)) return false;
      if (filters.prezzoMax && p.prezzo > Number(filters.prezzoMax)) return false;
      if (filters.superficieMin && p.superficie < Number(filters.superficieMin)) return false;
      if (filters.locali && p.locali < filters.locali) return false;
      if (filters.camere && p.camere < filters.camere) return false;
      if (filters.bagni && p.bagni < filters.bagni) return false;
      if (filters.box && !(p.box || p.postoAuto)) return false;
      if (filters.ascensore && !p.ascensore) return false;
      if (filters.esterno && !(p.balcone || p.terrazzo || p.giardino)) return false;
      if (filters.nuovaCostruzione && !p.nuovaCostruzione) return false;
      if (filters.daRistrutturare && !p.daRistrutturare) return false;
      return true;
    });
    const s = [...list];
    if (sort === "prezzo-asc") s.sort((a, b) => a.prezzo - b.prezzo);
    else if (sort === "prezzo-desc") s.sort((a, b) => b.prezzo - a.prezzo);
    else if (sort === "superficie") s.sort((a, b) => b.superficie - a.superficie);
    else if (sort === "compatibilita") s.sort((a, b) => b.compatibilita - a.compatibilita);
    else s.sort((a, b) => b.recente - a.recente);
    return s;
  }, [filters, sort, properties]);

  const viewBtn = (key, Icon, label) => (
    <button
      onClick={() => setView(key)}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium transition-all ${
        view === key ? "bg-brand text-white" : "text-anthracite/60 hover:text-brand"
      }`}
      data-testid={`view-${key}`}
    >
      <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className="pt-28 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        {/* header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand">
            <span className="h-px w-6 bg-brand/50" /> Immobili in vendita e in affitto
          </span>
          <h1 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl tracking-tight mt-4">
            Trova la casa <span className="italic text-brand">giusta per te</span>
          </h1>
          <p className="mt-4 text-lg text-anthracite/70 max-w-xl">
            Esplora la nostra selezione. Filtra per zona, budget e caratteristiche e trova l'immobile più compatibile con le tue esigenze.
          </p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar filters (desktop) */}
          <aside className="hidden lg:block lg:col-span-4 xl:col-span-3">
            <div className="sticky top-24">
              <Filters filters={filters} setFilters={setFilters} onReset={() => setFilters(DEFAULT_FILTERS)} resultCount={results.length} />
            </div>
          </aside>

          {/* Main */}
          <div className="lg:col-span-8 xl:col-span-9">
            {/* toolbar */}
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
              <div className="flex items-center gap-2">
                {/* mobile filter trigger */}
                <Sheet>
                  <SheetTrigger asChild>
                    <button className="lg:hidden inline-flex items-center gap-2 rounded-full border border-border-subtle bg-white px-4 py-2 text-sm font-semibold" data-testid="mobile-filters-trigger">
                      <SlidersHorizontal className="h-4 w-4" /> Filtri
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[90vw] max-w-sm overflow-y-auto bg-warm p-4">
                    <Filters filters={filters} setFilters={setFilters} onReset={() => setFilters(DEFAULT_FILTERS)} resultCount={results.length} />
                  </SheetContent>
                </Sheet>
                <div className="flex items-center rounded-full border border-border-subtle bg-white p-1">
                  {viewBtn("grid", LayoutGrid, "Griglia")}
                  {viewBtn("list", List, "Elenco")}
                  {viewBtn("map", Map, "Mappa")}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <span className="text-anthracite/50 hidden sm:inline">Ordina:</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-10 rounded-full border border-border-subtle bg-white px-4 text-sm font-medium outline-none focus:border-brand"
                  data-testid="sort-select"
                >
                  {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
            </div>

            {/* results */}
            {results.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border-subtle bg-white p-12 sm:p-16 text-center" data-testid="no-results">
                <span className="grid place-items-center h-16 w-16 rounded-2xl bg-accent-subtle text-brand mx-auto">
                  <SearchX className="h-8 w-8" />
                </span>
                <h3 className="font-display text-2xl font-semibold text-anthracite mt-5">Nessun immobile trovato</h3>
                <p className="text-anthracite/60 mt-2 max-w-sm mx-auto">Non ci sono immobili che corrispondono ai filtri selezionati. Prova ad allargare la ricerca.</p>
                <button onClick={() => setFilters(DEFAULT_FILTERS)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand text-white font-semibold px-6 py-3 hover:bg-brand-hover transition-colors" data-testid="no-results-reset">
                  <RotateCcw className="h-4 w-4" /> Azzera i filtri
                </button>
              </div>
            ) : view === "map" ? (
              <MapView properties={results} />
            ) : (
              <div className={view === "grid" ? "grid grid-cols-1 sm:grid-cols-2 gap-6" : "space-y-6"}>
                {results.map((p, i) => (
                  <PropertyCard key={p.id} property={p} view={view} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
